import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useGetLivePoints } from '../../../hooks/queries/lives/lives/use-get-live-points'
import { useGetMultiLiveTrophySumAll } from '../../../hooks/queries/lives/lives/use-get-multi-live-trophy-sum-all'
import { useGlobalStore } from '../../../store/global-store'
import type { TypeLive } from '../../../types/lives/live'
import { useUpdateLivePoints } from './use-update-live-points'

export const TrophyIcon = memo(() => {
  const { liveId, typeLive } = useGlobalStore(
    useShallow((state) => ({
      liveId: state.liveId,
      typeLive: state.typeLive,
      token: state.token,
    })),
  )

  const isMultiLive = typeLive?.includes('multi')

  const { liveTrophy, refetch: refetchTrophy } = useGetMultiLiveTrophySumAll(
    typeLive as TypeLive,
    isMultiLive,
  )

  const { live: livePoints, refetch: refetchLivePoints } = useGetLivePoints(
    liveId as number,
    !isMultiLive,
  )

  // Reconcilia com o server um pouco DEPOIS de cada gift (dá tempo do write
  // assentar). Debounce: rajada de combos vira 1 refetch só. Cobre o caso do
  // 1º gift chegar antes da subscription conectar (evento perdido): o evento
  // seguinte refaz a query e o total volta a bater.
  const reconcileTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleReconcile = useCallback(() => {
    if (reconcileTimer.current) clearTimeout(reconcileTimer.current)
    reconcileTimer.current = setTimeout(() => {
      if (isMultiLive) refetchTrophy()
      else refetchLivePoints()
    }, 2500)
  }, [isMultiLive, refetchTrophy, refetchLivePoints])

  useEffect(() => {
    return () => {
      if (reconcileTimer.current) clearTimeout(reconcileTimer.current)
    }
  }, [])

  useUpdateLivePoints(scheduleReconcile)

  const { points, setLivePoints } = useGlobalStore(
    useShallow((state) => ({
      points: state.livePoints,
      setLivePoints: state.setLivePoints,
    })),
  )

  useEffect(() => {
    const snapshot = isMultiLive
      ? (liveTrophy?.quantity ?? 0)
      : (livePoints?.points ?? 0)

    // O pubsub incrementa na hora; a query pode voltar um snapshot de ANTES do
    // gift. Pontos de live só crescem — snapshot nunca REBAIXA o contador,
    // só corrige pra cima (senão o 1º gift some quando a query resolve).
    if (snapshot > useGlobalStore.getState().livePoints) {
      setLivePoints(snapshot)
    }
  }, [liveTrophy, livePoints, isMultiLive, setLivePoints])

  const pointsFormated = useMemo(
    () =>
      Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(points),
    [points],
  )

  const isAndroid = navigator.appVersion.includes('Android')

  return (
    <div className="w-screen h-screen flex items-center justify-center">
      <span
        className="text-white font-bold"
        style={{
          fontSize: isAndroid ? '1px' : '10px',
        }}
      >
        {pointsFormated}
      </span>
    </div>
  )
})
